export type { ExtensionBindings } from "./core/agent-session-types.js";
export {
	CacheWarmer,
	type CacheWarmingDecisionEvent,
	type CacheWarmingDecisionEventResult,
	type CacheWarmingStatus,
} from "./core/cache-warmer.ts";
export type { ExecuteToolOptions, ExtensionToolContext } from "./core/extensions/context-types.ts";
export type {
	HostDiagnostic,
	HostInput,
	HostInputOptions,
	QuestionAnswer,
	QuestionnaireError,
	QuestionnaireResult,
	QuestionParams,
} from "./core/extensions/host-input.js";
export {
	type ExtensionContextEffectPhase,
	publishExtensionContextEffect,
	registerExtensionContextRetirementEffect,
} from "./core/extensions/runner-context.ts";
export type {
	ToolAnnotations,
	ToolExposure,
	ToolLoadout,
	ToolLoadoutChanges,
	ToolNamespace,
	ToolRendererResolver,
	ToolRenderers,
} from "./core/extensions/tool-types.ts";
export { resolveRestoredModelReference } from "./core/model-resolver-initial.ts";
export type { UsageEntry } from "./core/session-manager.ts";
export { resolveSessionStore } from "./core/session-storage.js";
export { CACHE_WARMING_MODES, type CacheWarmingMode } from "./core/settings-manager.ts";
export * from "./core/storage/index.js";
// Outside the storage barrel: workflow durability setup imports that barrel in every test worker.
export {
	type ProcessStorageChoice,
	processSharedStorageSelection,
	type StorageDomain,
	selectProcessStorage,
} from "./core/storage/process-selection.js";
export { JsonlSessionStore } from "./core/storage/session/jsonl-session-store.js";
// Internal trusted-host task integration (not model authority).
export { AgentTaskHost, type AgentTaskHostBinding, type AgentTaskRunnerFactory } from "./core/tasks/agent-adapter.js";
export type { AgentIntent, OperationId, TaskId, TaskResult, WaitPolicy } from "./core/tasks/contracts.js";
export { type AdmittedAgentTask, collectAgentTasks } from "./core/tasks/execution-scope.js";
export { bindOwnerTaskStore, getOwnerTaskStore, OwnerTaskStore } from "./core/tasks/owner-store.js";
export { formatReadLineRange, formatToolCallWithArgs } from "./core/tools/render-utils.ts";
export {
	createTranscript,
	type Transcript,
	type TranscriptOptions,
	type TranscriptPart,
	type TranscriptTextPart,
	type TranscriptThinkingPart,
	type TranscriptToolCallPart,
	type TranscriptToolResult,
	type TranscriptToolResultFormat,
} from "./core/transcript.js";
export { type CodemodeExtensionOptions, createCodemodeExtension } from "./extensions/codemode/index.js";
export {
	type CodemodeToolDetails,
	type CodemodeToolOptions,
	createCodemodeDescription,
	createCodemodeTool,
	createCodemodeToolDefinition,
} from "./extensions/codemode/tool.js";
export { createToolSearchExtension } from "./extensions/tool-search/index.js";
export { createToolSearchToolDefinition, type ToolSearchToolOptions } from "./extensions/tool-search/tool.js";
export { renderTaskFooter, TaskList, taskListSections } from "./modes/interactive/components/task-list.js";
export { TaskRow } from "./modes/interactive/components/task-row.js";
export {
	applyAssistantMessageDelta,
	beginStreamingAssistantMessage,
} from "./modes/interactive/streaming-assistant-message.ts";
// Core session management

export { type Args, parseArgs } from "./cli/args.ts";
// Config paths
export {
	APP_NAME,
	APP_TITLE,
	CONFIG_DIR_NAME,
	CONFIG_DIR_NAMES,
	getAgentConfigPaths,
	getAgentDir,
	getAgentDirs,
	getBundledInteractiveAssetPath,
	getChangelogPath,
	getDocsPath,
	getEnvNames,
	getEnvValue,
	getExamplesPath,
	getExportTemplateDir,
	getInteractiveAssetsDir,
	getLegacyAgentDir,
	getPackageDir,
	getPackageJsonPath,
	getProjectConfigDirs,
	getProjectConfigPaths,
	getReadmePath,
	getThemesDir,
	getUserConfigDirs,
	getUserConfigPaths,
	hasEnvValue,
	isBunBinary,
	LEGACY_CONFIG_DIR_NAME,
	LEGACY_ENV_PREFIX,
	PACKAGE_NAME,
	setEnvValue,
	VERSION,
	WORKFLOW_STAGE_SUBAGENT_GUARD_ENV,
} from "./config.js";
export {
	AgentSession,
	type AgentSessionConfig,
	type AgentSessionEvent,
	type AgentSessionEventListener,
	type CompactionReason,
	type ModelCycleResult,
	type ParsedSkillBlock,
	type PromptOptions,
	parseSkillBlock,
	type SessionCompactOptions,
	type SessionRecoveryReport,
	type SessionStats,
} from "./core/agent-session.js";
// Auth and model runtime
export { AuthStorage, readStoredCredential } from "./core/auth-storage.ts";
export {
	type AuthStorageBackend,
	FileAuthStorageBackend,
	InMemoryAuthStorageBackend,
} from "./core/auth-storage-backends.ts";
export { type BashResult, executeBashWithOperations } from "./core/bash-executor.ts";
export { getBuiltinPackagePaths } from "./core/builtin-packages.ts";
export {
	type CallbackActivityDescriptor,
	type CallbackActivityKind,
	runCallback,
	runSynchronousCallback,
} from "./core/callback-activity.ts";
// Cloudflare AI Gateway over the Workers AI binding (no API token; see docs/providers.md)
export {
	type AiGatewayBinding,
	type AiGatewayBindingGateway,
	type AiGatewayUniversalRequestLike,
	CLOUDFLARE_GATEWAY_BINDING_AUTH_SENTINEL,
	createGatewayBindingFetch,
	type GatewayBindingFetchOptions,
} from "./core/cloudflare-gateway-binding.ts";
// Compaction
export {
	type BranchPreparation,
	type BranchSummaryResult,
	type CollectEntriesResult,
	type CompactedTranscript,
	type CompactionPlannerModel,
	type CompactionPlanOptions,
	type CompactionRung,
	type CompactionRungResult,
	type CompactionRunRequest,
	type CompactionUrgency,
	calculateContextTokens,
	collectEntriesForBranchSummary,
	DEFAULT_COMPACTION_SETTINGS,
	estimateTokens,
	type FileOperations,
	type GenerateBranchSummaryOptions,
	generateBranchSummary,
	getLastAssistantUsage,
	type LineRange,
	type PlannerAuth,
	type PlannerBudget,
	type PlannerLimitClass,
	type PlannerOutcome,
	prepareBranchEntries,
	prepareCompactionBoundary,
	runVerbatimCompaction,
	serializeConversation,
	serializeConversationForCompaction,
	shouldCompact,
	startNewContextWindow,
	type VerbatimCompactionDetails,
	type VerbatimCompactionParameters,
	type VerbatimCompactionPreparation,
	type VerbatimCompactionResult,
} from "./core/compaction/index.ts";
export { createEventBus, type EventBus, type EventBusController } from "./core/event-bus.js";
export {
	AutoRoutingInferenceError,
	type ModelRoute,
	type ModelRoutingContext,
	type RoutingInferenceBounds,
	routeExecutionModel,
} from "./core/execution-model-router.js";
export { areExperimentalFeaturesEnabled } from "./core/experimental.ts";
export { sessionScopedExtensionState } from "./core/extension-session-state.ts";
export {
	ExtensionStartupError,
	type ExtensionStartupFailure,
	isExtensionStartupFailure,
	isTransientExtensionStartupFailure,
} from "./core/extension-startup-failure.ts";
// Run modes for programmatic SDK usage
export { noOpUIContext } from "./core/extensions/runner-ui.ts";
export {
	getModelFastRoute,
	resolveUpstreamModelId,
	usesChatGptCodexTransport,
	usesFirstPartyCodexRouting,
	withCodexFastRouteHeaders,
	withFastRouteStreamOptions,
} from "./core/fast-model-routing.ts";
export {
	CODEX_FAST_ROUTE_HEADER,
	CODEX_FAST_ROUTE_ORIGINATOR,
} from "./core/fast-model-routing-transport.ts";
export {
	ANTHROPIC_FAST_MODE_MODEL_IDS,
	copilotAdvertisedFastModelIds,
	deriveFastModelVariants,
	FAST_MODEL_ID_SUFFIX,
	FAST_MODEL_SERVICE_TIER,
	FAST_MODEL_SPEED,
	type FastModelVariantDerivation,
	type FastModelVariantDiagnostic,
	type FastModelVariantsOptions,
	fastModelId,
	isNativeFastRouteApi,
	usesAnthropicFastMode,
	usesOpenAIFastServiceTier,
	usesXaiFastServiceTier,
	withFastModelVariants,
} from "./core/fast-model-variants.ts";
export {
	parseFlattenedKeyPath,
	reconstructFlattenedKeys,
	unflattenArgumentsWithSchema,
} from "./core/flattened-tool-arguments.ts";
// Footer data provider (git branch + extension statuses - data not otherwise available to extensions)
export type { ReadonlyFooterDataProvider } from "./core/footer-data-provider.ts";
export {
	RUNTIME_INTERCOM_GROUP_ENV,
	readRuntimeIntercomGroup,
	runtimeIntercomGroupEnvKey,
} from "./core/intercom-runtime-group.ts";
export { keybindingIdentity } from "./core/keybinding-identity.js";
export { matchesMcpDirectToolSelection } from "./core/mcp-child-policy.ts";
export {
	type McpExposure,
	type McpOAuthConfig,
	type McpServerConfig,
	type McpServerContribution,
	mcpNamespace,
	validateMcpServerConfig,
} from "./core/mcp-servers.ts";
export { convertToLlm } from "./core/messages.ts";
export type {
	ModelFallbackFailureKind,
	ModelFallbackFailureSignal,
	ModelFallbackFailureSource,
} from "./core/model-fallback-failures.ts";
export {
	errorMessage,
	isRetryableModelFailure,
	isRetryableSameModelFailure,
	modelFailureMessage,
	normalizeModelFailureSignal,
} from "./core/model-fallback-failures.ts";
export { ModelRegistry } from "./core/model-registry.ts";
export {
	eligiblePair,
	type ModelConstraints,
	ModelConstraintsSchema,
	type ModelRouterOutput,
	parseModelConstraints,
	setsProviders,
} from "./core/model-routing-constraints.js";
export {
	isModelRoutingDebugEnabled,
	MODEL_ROUTING_DEBUG_ENV,
	reportModelRoutingDebug,
} from "./core/model-routing-debug.js";
export {
	DIFFICULTY_LEVELS,
	type Difficulty,
	MISTAKE_COST_LEVELS,
	type MistakeCost,
	mergeTaskNeeds,
	parseTaskNeeds,
	type TaskNeeds,
	TaskNeedsSchema,
	WORK_KINDS,
	type WorkKind,
} from "./core/model-routing-needs.js";
export {
	type CreateModelRuntimeOptions,
	CredentialSynchronizationError,
	type CredentialSynchronizationOperation,
	ModelRuntime,
	type ModelRuntimeAuthOverrides,
} from "./core/model-runtime.js";
export type {
	PackageManager,
	PathMetadata,
	ProgressCallback,
	ProgressEvent,
	ResolvedMcpServer,
	ResolvedPaths,
	ResolvedResource,
} from "./core/package-manager.ts";
export { DefaultPackageManager } from "./core/package-manager.ts";
export {
	configureProviderRequestBudget,
	isProviderRateLimit,
	type ProviderRateLimitSnapshot,
	type ProviderRequestBudget,
	providerCooldownMs,
	providerIdForModel,
	providerRateLimitSnapshot,
	providerRetryHint,
	recordProviderRateLimit,
} from "./core/provider-rate-limits.js";
export type {
	DefaultResourceLoaderInheritanceSnapshot,
	ResourceCollision,
	ResourceDiagnostic,
	ResourceLoader,
	SkillCandidate,
	SkillCatalog,
	SkillCatalogCommand,
	SkillResolution,
} from "./core/resource-loader.ts";
export {
	buildSkillCatalog,
	DefaultResourceLoader,
	getSkillCatalog,
	loadProjectContextFiles,
} from "./core/resource-loader.ts";
export { nextRetryDecision, type RetryDecision, type RetryPolicySettings } from "./core/retry-policy.ts";
// SDK for programmatic usage
export {
	AgentSessionRuntime,
	type AgentSessionRuntimeDiagnostic,
	type AgentSessionServices,
	type AtomicBuiltin,
	type CreateAgentSessionFromServicesOptions,
	type CreateAgentSessionOptions,
	type CreateAgentSessionResult,
	type CreateAgentSessionRuntimeFactory,
	type CreateAgentSessionRuntimeResult,
	type CreateAgentSessionServicesOptions,
	// Factory
	createAgentSession,
	createAgentSessionFromServices,
	createAgentSessionRuntime,
	createAgentSessionServices,
	createBashTool,
	// Tool factories (for custom cwd)
	createCodingTools,
	createEditTool,
	createFindTool,
	createLsTool,
	createPowerShellTool,
	createReadOnlyTools,
	createReadTool,
	createSearchTool,
	createStructuredOutputCapture,
	createStructuredOutputTool,
	createWriteTool,
	type JsonObject,
	type JsonPrimitive,
	type JsonValue,
	type MessageEndEvent,
	type MessageStartEvent,
	type MessageUpdateEvent,
	type PromptTemplate,
	type ResourceAdmission,
	type ResourceAdmissionRequest,
	type ResourcePermit,
	type ResourceProcessIdentity,
	STRUCTURED_OUTPUT_TOOL_NAME,
	type StructuredOutputCapture,
	type StructuredOutputFileCapture,
	type StructuredOutputToolOptions,
	type ToolExecutionEndEvent,
	type ToolExecutionStartEvent,
	type ToolExecutionUpdateEvent,
	withResourceAdmission,
} from "./core/sdk.ts";
export {
	type BranchSummaryEntry,
	buildContextEntries,
	buildSessionContext,
	buildSessionProjection,
	type CompactionEntry,
	type ContextEditableContent,
	type ContextEditEntry,
	CURRENT_SESSION_VERSION,
	type CustomEntry,
	type CustomMessageEntry,
	type FileEntry,
	getDefaultSessionDirPath,
	getLatestCompactionBoundaryEntry,
	type ModelChangeEntry,
	type NewSessionOptions,
	type ProjectedSessionEntry,
	parseSessionEntries,
	type SessionContext,
	type SessionEntry,
	type SessionEntryBase,
	SessionEntryError,
	type SessionHeader,
	SessionIdError,
	type SessionInfo,
	type SessionInfoEntry,
	SessionManager,
	type SessionMessageEntry,
	type SessionNameState,
	type SessionProjection,
	type SessionSummaryEntry,
	type SessionTreeNode,
	type SessionWorkflowMetadata,
	sessionEntryToContextMessages,
	type ThinkingLevelChangeEntry,
} from "./core/session-manager.ts";
export {
	WORKFLOW_SESSION_METADATA_ENV,
	workflowSessionMetadataFromEnv,
} from "./core/session-manager-classification.ts";
export type { DefaultProjectTrust, QuietStartup } from "./core/settings-manager.ts";
export {
	type CompactionModelOverride,
	type CompactionSettings,
	type ImageSettings,
	type ModelRoutingSettings,
	type PackageSource,
	type RetrySettings,
	type Settings,
	SettingsManager,
	type SettingsManagerCreateOptions,
} from "./core/settings-manager.ts";
// Skills
export {
	formatSkillsForPrompt,
	type LoadSkillsFromDirOptions,
	type LoadSkillsResult,
	loadSkills,
	loadSkillsFromDir,
	type Skill,
	type SkillFrontmatter,
} from "./core/skills.ts";
export { createSyntheticSourceInfo } from "./core/source-info.ts";
export {
	generateStructuredOutput,
	type StructuredOutputRequest,
	type StructuredOutputResult,
} from "./core/structured-output/index.js";
export type { NormalizedBuildSystemPromptOptions } from "./core/system-prompt.ts";
export {
	formatToolStatus,
	type ToolSelectionSource,
	type ToolSelectionStatus,
	type ToolStatus,
	type ToolStatusFormatStyle,
	type ToolStatusReport,
	type UnavailableToolStatus,
} from "./core/tool-status.js";
export { type EditDiffResult, generateDiffString, generateUnifiedPatch } from "./core/tools/edit-diff.ts";
// Builtin tool definitions reusable by first-party extensions (e.g. workflows
// invoking the structured ask_user_question UI deterministically).
// Tools
export {
	BASH_SHELL_PRESENTATION,
	type BashOperations,
	type BashSpawnContext,
	type BashSpawnHook,
	type BashToolDetails,
	type BashToolInput,
	type BashToolOptions,
	bashToolSystemPromptContribution,
	createAskUserQuestionToolDefinition,
	createBashToolDefinition,
	createEditToolDefinition,
	createFindToolDefinition,
	createKillTool,
	createKillToolDefinition,
	createLocalBashOperations,
	createLocalPowerShellOperations,
	createLsToolDefinition,
	createPowerShellToolDefinition,
	createReadToolDefinition,
	createSearchToolDefinition,
	createWriteToolDefinition,
	DEFAULT_MAX_BYTES,
	DEFAULT_MAX_LINES,
	type EditOperations,
	type EditToolDetails,
	type EditToolInput,
	type EditToolOptions,
	editToolSystemPromptContribution,
	type FindOperations,
	type FindToolDetails,
	type FindToolInput,
	type FindToolOptions,
	findToolSystemPromptContribution,
	formatSize,
	type KillToolOptions,
	type LsOperations,
	type LsToolDetails,
	type LsToolInput,
	type LsToolOptions,
	lsToolSystemPromptContribution,
	type PowerShellOperations,
	type PowerShellSpawnContext,
	type PowerShellSpawnHook,
	type PowerShellToolDetails,
	type PowerShellToolInput,
	type PowerShellToolOptions,
	type ReadOperations,
	type ReadToolDetails,
	type ReadToolInput,
	type ReadToolOptions,
	readToolSystemPromptContribution,
	type SearchToolDetails,
	type SearchToolInput,
	type SearchToolOptions,
	type ShellToolPresentation,
	searchToolSystemPromptContribution,
	type ToolsOptions,
	type TruncationOptions,
	type TruncationResult,
	truncateHead,
	truncateLine,
	truncateTail,
	type WriteFileOptions,
	type WriteOperations,
	type WriteToolInput,
	type WriteToolOptions,
	withFileMutationQueue,
	writeToolSystemPromptContribution,
} from "./core/tools/index.js";
export {
	hasProjectTrustInputs,
	hasTrustRequiringProjectResources,
	type ProjectTrustDecision,
	ProjectTrustStore,
	type ProjectTrustStoreEntry,
	type ProjectTrustUpdate,
	TRUST_REQUIRING_PROJECT_CONFIG_RESOURCES,
} from "./core/trust-manager.ts";
export { StringEnum, type StringEnumOptions } from "./core/typebox-compat.ts";
export {
	type LoadedMcpConfig,
	type McpServerConfigPatch,
	type McpServerEntry,
	updateMcpServerConfig,
} from "./extensions/mcp/config.ts";
export { createMcpExtension, type McpExtensionOptions } from "./extensions/mcp/index.js";
export { McpOAuthCredentialStore } from "./extensions/mcp/oauth.ts";
export * from "./index-extensions.js";
// Main entry point
export { type MainOptions, main } from "./main.ts";
export {
	InteractiveMode,
	type InteractiveModeOptions,
	type ModelInfo,
	type PrintModeOptions,
	RpcClient,
	type RpcClientOptions,
	type RpcCommand,
	type RpcEvent,
	type RpcEventListener,
	type RpcExtensionUIRequest,
	type RpcExtensionUIResponse,
	type RpcResponse,
	type RpcSessionState,
	runPrintMode,
	runRpcMode,
} from "./modes/index.ts";
// UI components for extensions
export {
	ArminComponent,
	AssistantMessageComponent,
	BashExecutionComponent,
	BorderedLoader,
	BranchSummaryMessageComponent,
	type ChatMessageEntry,
	type ChatMessageRenderOptions,
	// Internal host seam for bundled workflow stage chat; not yet a stable extension API.
	ChatSessionHost,
	type ChatSessionHostBashRequest,
	type ChatSessionHostCommands,
	type ChatSessionHostEntry,
	type ChatSessionHostOpts,
	type ChatSessionHostStyle,
	type ChatSessionSubmitMode,
	ChatTranscriptComponent,
	type ChatTranscriptEntryLike,
	type ChatTranscriptRenderer,
	type ChatTranscriptRole,
	CustomEditor,
	type CustomEditorOptions,
	CustomEntryComponent,
	CustomMessageComponent,
	chatEntriesFromAgentMessages,
	DynamicBorder,
	ExtensionEditorComponent,
	ExtensionInputComponent,
	ExtensionSelectorComponent,
	FooterComponent,
	keyHint,
	keyHintIfBound,
	keyText,
	LiveChatEntriesController,
	LoginDialogComponent,
	ModelSelectorComponent,
	OAuthSelectorComponent,
	type RenderDiffOptions,
	rawKeyHint,
	renderChatMessageEntry,
	renderDiff,
	ScrollableChatTranscriptComponent,
	ScrollableComponentViewport,
	SessionSelectorComponent,
	type SettingsCallbacks,
	type SettingsConfig,
	SettingsSelectorComponent,
	ShowImagesSelectorComponent,
	SkillInvocationMessageComponent,
	ThemeSelectorComponent,
	ThinkingSelectorComponent,
	ToolExecutionComponent,
	type ToolExecutionOptions,
	TRANSCRIPT_JUMP_TO_END_URL,
	TranscriptFollowIndicator,
	TreeSelectorComponent,
	truncateToVisualLines,
	UsageMeterComponent,
	UserMessageComponent,
	UserMessageSelectorComponent,
	type VisualTruncateResult,
	WorkingStatusComponent,
	type WorkingStatusComponentOptions,
} from "./modes/interactive/components/index.ts";
export {
	classifyChatCommand,
	createSessionSkillAutocompleteProvider,
	getSessionSkillCommands,
} from "./modes/interactive/skill-command-autocomplete.ts";
// Theme utilities for custom tools and extensions
export {
	getLanguageFromPath,
	getMarkdownTheme,
	getSelectListTheme,
	getSettingsListTheme,
	highlightCode,
	initTheme,
	Theme,
	type ThemeColor,
} from "./modes/interactive/theme/theme.js";
export { pickWhimsicalWorkingMessage } from "./modes/interactive/whimsical-messages.ts";
export { createChildProcessEnvironment, spawnProcess } from "./utils/child-process.ts";
// Clipboard utilities
export { copyToClipboard } from "./utils/clipboard.js";
export { parseFrontmatter, stripFrontmatter } from "./utils/frontmatter.ts";
export {
	isSafeFsWatchPathError,
	isUnsafeWindowsShortPath,
	resolveNativeWatchPath,
	SAFE_FS_WATCH_CANONICALIZATION_FAILED,
	SAFE_FS_WATCH_UNSAFE_WINDOWS_SHORT_PATH,
	type SafeFsWatchErrorCode,
	type SafeFsWatchPathError,
	watchWithErrorHandler,
} from "./utils/fs-watch.ts";
export { createGitEnvironment, GIT_LOCAL_ENV_VARS } from "./utils/git-env.ts";
export { convertToPng } from "./utils/image-convert.ts";
export { formatDimensionNote, type ResizedImage, resizeImage } from "./utils/image-resize.ts";
export { INTERACTIVE_ENGINE_ENV_VARS, scrubInteractiveEngineEnv } from "./utils/interactive-engine-env.ts";
export { detectSupportedImageMimeTypeFromFile } from "./utils/mime.ts";
// Shell utilities
export { getPowerShellConfig, getShellConfig } from "./utils/shell.ts";
